// SB6-ANA-TILE-02 — Undo / Redo icon pair in the AnalysisHeader top bar.
//
// TRIAGE-02 (audit 3.2 / IMP-02): both halves are live now. The stack is
// GLOBAL (bounded 50, survives image switches) and the redo stack is
// populated on every undo. Tooltips carry the top entry's description;
// empty stacks render the disabled affordance ("Nothing to undo/redo").
import { IconButton, Stack, Tooltip } from "@mui/material";
import {
  describeUndoEntry,
  useUndoStackStore,
} from "../state/undoStackStore.js";
import { Redo2Icon, Undo2Icon } from "@aeriosiq/ui/icons";

export function UndoRedoCluster(): React.ReactElement {
  const undoTop = useUndoStackStore((s) => s.entries[s.entries.length - 1]);
  const redoTop = useUndoStackStore(
    (s) => s.redoEntries[s.redoEntries.length - 1],
  );
  const triggerUndo = useUndoStackStore((s) => s.triggerUndo);
  const triggerRedo = useUndoStackStore((s) => s.triggerRedo);

  const undoEnabled = undoTop !== undefined && triggerUndo !== null;
  const redoEnabled = redoTop !== undefined && triggerRedo !== null;
  const undoDescription = undoTop ? describeUndoEntry(undoTop) : "";
  const redoDescription = redoTop ? describeUndoEntry(redoTop) : "";

  return (
    <Stack direction="row" spacing={0}>
      <Tooltip
        title={
          undoEnabled ? `Undo (Ctrl+Z) — ${undoDescription}` : "Nothing to undo"
        }
        arrow
      >
        {/* span wrapper so Tooltip still fires when aria-disabled. */}
        <span>
          <IconButton
            size="small"
            onClick={() => {
              if (!undoEnabled) return;
              useUndoStackStore.getState().triggerUndo?.();
            }}
            aria-label={
              undoEnabled
                ? `Undo last action: ${undoDescription}`
                : "Nothing to undo"
            }
            aria-disabled={!undoEnabled}
            data-testid="analysis-header-undo"
            sx={{
              ...(undoEnabled
                ? {}
                : {
                    cursor: "not-allowed",
                    opacity: 0.5,
                  }),
            }}
          >
            <Undo2Icon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip
        title={
          redoEnabled
            ? `Redo (Ctrl+Shift+Z) — ${redoDescription}`
            : "Nothing to redo"
        }
        arrow
      >
        <span>
          <IconButton
            size="small"
            onClick={() => {
              if (!redoEnabled) return;
              useUndoStackStore.getState().triggerRedo?.();
            }}
            aria-label={
              redoEnabled
                ? `Redo last undone action: ${redoDescription}`
                : "Nothing to redo"
            }
            aria-disabled={!redoEnabled}
            data-testid="analysis-header-redo"
            sx={{
              ...(redoEnabled
                ? {}
                : {
                    cursor: "not-allowed",
                    opacity: 0.5,
                  }),
            }}
          >
            <Redo2Icon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
}
